/**
 * @module stores/collectionStore
 * @description Collection state management
 *
 * Manages collection state including:
 * - Collection list and tree structure
 * - Collection selection
 * - Special collections (All Items, Trash, Unfiled, etc.)
 * - Expanded state for tree navigation
 *
 * @example
 * import { collectionStore, collectionTree, selectCollection } from '@stores/collectionStore';
 *
 * // Get collections tree
 * const names = $collectionTree.map(node => node.data.name);
 *
 * // Select a collection
 * selectCollection('ABC123');
 *
 * // Select special collection
 * selectSpecialCollection('all');
 */

import { writable, derived, get } from 'svelte/store';
import { createLogger } from '@services/loggerService';
import { notify } from '@services/notificationService';
import { indexedDBService } from '@services/indexedDBService';
import { zoteroService } from '@services/zoteroService';
import {
  buildCollectionTree,
  flattenCollectionTree,
  type CollectionNode,
} from '@services/collectionActionsService';
import { createCancellableRequest, isAbortError } from '@/utils';
import type { Collection, LibraryType, SpecialCollectionKey } from '@types';

const log = createLogger('store');

// =============================================================================
// REQUEST CONTROLLER
// =============================================================================

/**
 * Cancellable request controller for managing in-flight collection requests.
 * Automatically cancels previous requests when a new one is initiated,
 * preventing race conditions.
 */
const collectionsRequest = createCancellableRequest();

// =============================================================================
// TYPES
// =============================================================================

/** Collection state */
export interface CollectionState {
  /** Flat list of all collections */
  collections: Collection[];
  /** Currently selected collection key (or special key) */
  selectedKey: string | SpecialCollectionKey | null;
  /** Whether a special collection is selected */
  isSpecialSelected: boolean;
  /** Array of expanded collection keys in tree view (serializable) */
  expandedKeys: string[];
  /** Whether collections are loading */
  isLoading: boolean;
  /** Error message */
  error: string | null;
  /** Library ID these collections belong to */
  libraryId: number | null;
  /** Library type */
  libraryType: LibraryType | null;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/** Special collection keys */
export const SPECIAL_COLLECTIONS: SpecialCollectionKey[] = [
  'all',
  'unfiled',
  'trash',
  'duplicates',
  'publications',
];

/** Check if a key is a special collection */
export function isSpecialCollection(key: string | null): key is SpecialCollectionKey {
  return key !== null && SPECIAL_COLLECTIONS.includes(key as SpecialCollectionKey);
}

// =============================================================================
// INITIAL STATE
// =============================================================================

const initialState: CollectionState = {
  collections: [],
  selectedKey: 'all',
  isSpecialSelected: true,
  expandedKeys: [],
  isLoading: false,
  error: null,
  libraryId: null,
  libraryType: null,
};

// =============================================================================
// STORE
// =============================================================================

/** Internal writable store */
const store = writable<CollectionState>(initialState);

/** Exported collection store (read-only subscribe) */
export const collectionStore = {
  subscribe: store.subscribe,
};

// =============================================================================
// DERIVED STORES
// =============================================================================

/** Flat list of collections */
export const collections = derived(store, ($store) => $store.collections);

/** Collection count */
export const collectionCount = derived(store, ($store) => $store.collections.length);

/** Collections as tree structure */
export const collectionTree = derived(store, ($store) => buildCollectionTree($store.collections));

/** Flattened tree for rendering (respects expanded state) */
export const flattenedCollections = derived(store, ($store) => {
  const tree = buildCollectionTree($store.collections);
  // Convert array to Set for efficient lookups in flattenCollectionTree
  return flattenCollectionTree(tree, new Set($store.expandedKeys));
});

/** Currently selected collection key */
export const selectedCollectionKey = derived(store, ($store) => $store.selectedKey);

/** Whether a special collection is selected */
export const isSpecialCollectionSelected = derived(store, ($store) => $store.isSpecialSelected);

/** Currently selected collection object (null if special collection) */
export const selectedCollection = derived(store, ($store) => {
  if ($store.isSpecialSelected || !$store.selectedKey) return null;
  return $store.collections.find((c) => c.key === $store.selectedKey) ?? null;
});

/** Set of expanded collection keys (converted from array for efficient lookups) */
export const expandedKeys = derived(store, ($store) => new Set($store.expandedKeys));

/** Whether collections are loading */
export const isCollectionsLoading = derived(store, ($store) => $store.isLoading);

/** Collection error */
export const collectionError = derived(store, ($store) => $store.error);

// =============================================================================
// ACTIONS
// =============================================================================

/**
 * Load collections for a library
 */
export async function loadCollections(libraryId: number, libraryType: LibraryType): Promise<void> {
  log.info('Loading collections', { libraryId, libraryType });

  // Get signal from cancellable request (automatically cancels any in-flight request)
  const signal = collectionsRequest.getSignal();

  store.update((s) => ({
    ...s,
    isLoading: true,
    error: null,
    libraryId,
    libraryType,
  }));

  const result = await zoteroService.getCollections(libraryId, libraryType, { signal });

  // Check if the request was aborted - if so, don't update state
  if (signal.aborted) {
    log.debug('Collections request was aborted, ignoring result');
    return;
  }

  if (result.success) {
    store.update((s) => ({
      ...s,
      collections: result.data,
      isLoading: false,
    }));
    log.info('Collections loaded', { count: result.data.length });
  } else {
    // Don't show error for aborted requests
    if (isAbortError(result.error)) {
      log.debug('Collections request aborted');
      return;
    }
    store.update((s) => ({
      ...s,
      isLoading: false,
      error: result.error,
    }));
    notify.error(`Failed to load collections: ${result.error}`);
    log.error('Failed to load collections', { error: result.error });
  }
}

/**
 * Refresh collections
 */
export async function refreshCollections(): Promise<void> {
  const state = get(store);
  if (state.libraryId && state.libraryType) {
    await loadCollections(state.libraryId, state.libraryType);
  }
}

/**
 * Select a collection by key
 */
export function selectCollection(key: string): void {
  log.debug('Selecting collection', { key });

  const isSpecial = isSpecialCollection(key);

  store.update((s) => ({
    ...s,
    selectedKey: key,
    isSpecialSelected: isSpecial,
  }));

  // Persist selection (only for regular collections)
  if (!isSpecial) {
    indexedDBService.setSelectedCollection(key);
  }
}

/**
 * Select a special collection
 */
export function selectSpecialCollection(key: SpecialCollectionKey): void {
  log.debug('Selecting special collection', { key });

  store.update((s) => ({
    ...s,
    selectedKey: key,
    isSpecialSelected: true,
  }));
}

/**
 * Clear collection selection (defaults to 'all')
 */
export function clearCollectionSelection(): void {
  store.update((s) => ({
    ...s,
    selectedKey: 'all',
    isSpecialSelected: true,
  }));
}

/**
 * Toggle expansion of a collection in tree view
 */
export function toggleExpanded(key: string): void {
  store.update((s) => {
    const isExpanded = s.expandedKeys.includes(key);
    const newExpanded = isExpanded
      ? s.expandedKeys.filter((k) => k !== key)
      : [...s.expandedKeys, key];
    return { ...s, expandedKeys: newExpanded };
  });
}

/**
 * Expand a collection
 */
export function expandCollection(key: string): void {
  store.update((s) => {
    if (s.expandedKeys.includes(key)) return s;
    return { ...s, expandedKeys: [...s.expandedKeys, key] };
  });
}

/**
 * Collapse a collection
 */
export function collapseCollection(key: string): void {
  store.update((s) => ({
    ...s,
    expandedKeys: s.expandedKeys.filter((k) => k !== key),
  }));
}

/**
 * Expand all collections
 */
export function expandAll(): void {
  store.update((s) => ({
    ...s,
    expandedKeys: s.collections.map((c) => c.key),
  }));
}

/**
 * Collapse all collections
 */
export function collapseAll(): void {
  store.update((s) => ({
    ...s,
    expandedKeys: [],
  }));
}

/**
 * Expand ancestors of a collection (for revealing in tree)
 */
export function expandAncestors(key: string): void {
  const state = get(store);
  const collection = state.collections.find((c) => c.key === key);
  if (!collection) return;

  const ancestorKeys: string[] = [];
  const visited = new Set<string>();
  let current: Collection | undefined = collection;

  while (current?.data?.parentCollection && !visited.has(current.data.parentCollection)) {
    visited.add(current.data.parentCollection);
    ancestorKeys.push(current.data.parentCollection);
    current = state.collections.find((c) => c.key === current?.data?.parentCollection);
  }

  if (ancestorKeys.length > 0) {
    store.update((s) => {
      // Add only ancestors that aren't already expanded
      const currentSet = new Set(s.expandedKeys);
      const keysToAdd = ancestorKeys.filter((k) => !currentSet.has(k));
      if (keysToAdd.length === 0) return s;
      return { ...s, expandedKeys: [...s.expandedKeys, ...keysToAdd] };
    });
  }
}

/**
 * Add a collection to the store (after creation)
 */
export function addCollection(collection: Collection): void {
  store.update((s) => ({
    ...s,
    collections: [...s.collections, collection],
  }));
}

/**
 * Update a collection in the store
 */
export function updateCollection(key: string, data: Partial<Collection>): void {
  store.update((s) => ({
    ...s,
    collections: s.collections.map((c) => (c.key === key ? { ...c, ...data } : c)),
  }));
}

/**
 * Remove a collection from the store
 */
export function removeCollection(key: string): void {
  store.update((s) => {
    // If the removed collection was selected, switch to 'all'
    const newSelectedKey = s.selectedKey === key ? 'all' : s.selectedKey;
    const newIsSpecial = newSelectedKey === 'all' || isSpecialCollection(newSelectedKey as string);

    return {
      ...s,
      collections: s.collections.filter((c) => c.key !== key),
      selectedKey: newSelectedKey,
      isSpecialSelected: newIsSpecial,
    };
  });
}

/**
 * Clear collection error
 */
export function clearCollectionError(): void {
  store.update((s) => ({ ...s, error: null }));
}

/**
 * Get current collection state
 */
export function getCollectionState(): CollectionState {
  return get(store);
}

/**
 * Get collection by key
 */
export function getCollectionByKey(key: string): Collection | null {
  const state = get(store);
  return state.collections.find((c) => c.key === key) ?? null;
}

// =============================================================================
// STORE RESET
// =============================================================================

/**
 * Reset store to initial state
 */
export function resetCollectionStore(): void {
  store.set({ ...initialState });
}

export default collectionStore;
